<template>
  <div id="system" class="tab-pane active">
    <div class="filter-box mb-3" data-test="log-filter-box">
      <form
        class="w-100"
        @submit.prevent="applyFilters"
        @keydown.enter.prevent="applyFilters"
      >
        <transition name="fade">
          <div v-show="showFilters">
            <div class="row g-2">
              <div class="col-12 col-md-6">
                <label class="form-label">Case</label>
                <input
                  v-model.trim="filters.case_no"
                  type="text"
                  class="form-control"
                  autocomplete="off"
                  placeholder="Case number, ID or Manifest ID..."
                  data-test="examinations-log-case-filter"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">
                  {{ trans("admin.draftExaminations.product") }}
                </label>
                <generic-admin-filter
                  v-model="filters.product_id"
                  :placeholder="trans('admin.draftExaminations.product')"
                  endpoint="products"
                  label-field="display_name"
                  value-field="id"
                />
              </div>
            </div>

            <div class="row g-2 mt-1">
              <div class="col-12 col-md-6">
                <label class="form-label">
                  {{ trans("examination.center") }}
                </label>
                <select2
                  :key="selectKeys.center"
                  v-model="filters.center_id"
                  :data-placeholder="trans('forms.chooseCenterPlaceholder')"
                  name="centers"
                >
                  <option :value="null"></option>
                  <option v-for="c in centers" :key="c.id" :value="c.id">
                    {{ c.name }}
                  </option>
                </select2>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">
                  {{ trans("admin.draftExaminations.user") }}
                </label>
                <input
                  v-model.trim="filters.user"
                  type="text"
                  class="form-control"
                  :placeholder="
                    trans('admin.draftExaminations.userPlaceholder')
                  "
                  autocomplete="off"
                />
              </div>
            </div>

            <div class="row g-2 mt-1">
              <div class="col-12 col-md-6">
                <label class="form-label">Status</label>
                <generic-admin-filter
                  v-model="filters.status_id"
                  placeholder="Choose a case status..."
                  endpoint="statuses"
                  label-field="name"
                  value-field="id"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Patient</label>
                <input
                  v-model.trim="filters.patient"
                  type="text"
                  class="form-control"
                  placeholder="Patient ID or SSN"
                  autocomplete="off"
                />
              </div>
            </div>

            <div class="row g-2 mt-1">
              <div class="col-12 col-md-6">
                <label class="form-label">
                  {{ trans("evaluation.tableColumnCreatedAtFrom") }}
                </label>
                <custom-datepicker
                  :date="filters.dateFrom"
                  :placeholder="trans('evaluation.tableColumnCreatedAtFrom')"
                  @on-date-change="changeDates('dateFrom', $event)"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">
                  {{ trans("evaluation.tableColumnCreatedAtTo") }}
                </label>
                <custom-datepicker
                  :date="filters.dateUntil"
                  :placeholder="trans('evaluation.tableColumnCreatedAtTo')"
                  @on-date-change="changeDates('dateUntil', $event)"
                />
              </div>
            </div>

            <div class="row g-2 mt-1">
              <div class="col-12 col-md-6">
                <label class="form-label">Medium</label>
                <input
                  v-model.trim="filters.medium"
                  type="text"
                  class="form-control"
                  placeholder="Search for Medium ID..."
                  autocomplete="off"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Part</label>
                <input
                  v-model.trim="filters.part"
                  type="text"
                  class="form-control"
                  placeholder="Search for Part ID..."
                  autocomplete="off"
                />
              </div>
            </div>
          </div>
        </transition>

        <div class="row mt-3">
          <div class="col-12">
            <div
              class="filter-actions d-flex justify-content-end align-items-center flex-wrap gap-2"
            >
              <button
                v-if="showFilters"
                class="btn btn-secondary"
                type="button"
                @click="clearFilters"
              >
                <i class="fal fa-times" /> {{ trans("general.clear") }}
              </button>
              <button
                v-if="showFilters"
                class="btn btn-primary"
                type="submit"
                data-test="examinations-log-search"
              >
                <i class="far fa-search" /> {{ trans("general.search") }}
              </button>
              <button
                class="btn btn-outline-secondary"
                type="button"
                data-test="examinations-log-toggle-filters"
                @click="showFilters = !showFilters"
              >
                <i
                  :class="showFilters ? 'fas fa-chevron-up' : 'fas fa-filter'"
                />
                {{
                  showFilters
                    ? trans("general.hideFilters") || "Hide filters"
                    : trans("general.showFilters") || "Show filters"
                }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>

    <clip-loader v-if="loading" />

    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead class="sortable-table-header">
          <tr>
            <th>{{ trans("admin.draftExaminations.examination") }}</th>
            <th>{{ trans("admin.draftExaminations.product") }}</th>
            <th>{{ trans("admin.draftExaminations.centre") }}</th>
            <th>{{ trans("admin.draftExaminations.user") }}</th>
            <th>{{ trans("admin.draftExaminations.initiated") }}</th>
            <th>{{ trans("admin.draftExaminations.status") }}</th>
            <th style="width: 36px">Dismiss</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in system.rows"
            :key="index"
            :class="rowClass(row)"
            :data-test="'examinations-log-row-' + row.case_no"
          >
            <td>
              <button
                class="btn btn-link p-0"
                :data-test="'examinations-log-open-' + row.case_no"
                @click="openDetail(row.examination_id)"
              >
                {{ row.case_no }}
              </button>
              &nbsp;
              <button
                class="btn btn-link p-0 text-muted"
                @click="copyToClipboard(row.case_no)"
              >
                <i class="far fa-copy" />
              </button>
            </td>
            <td>{{ row.product }}</td>
            <td>{{ row.center_name }}</td>
            <td :data-test="'examinations-log-user-' + row.case_no">
              {{ row.user_name }}
            </td>
            <td>{{ row.initiated_at }}</td>
            <td>{{ row.status_name }}</td>
            <td class="text-center col-actions">
              <button
                v-if="canDismiss(row)"
                class="btn btn-link p-0"
                :data-test="'examinations-log-dismiss-' + row.case_no"
                :title="
                  trans('admin.draftExaminations.dismissTooltip') ||
                  'Dismiss from priority. This does not change the examination; it only moves it to regular ordering.'
                "
                @click="toggleDismiss(row)"
              >
                <i class="fas fa-arrow-down text-muted" />
              </button>
              <span
                v-if="row.dismissed_at"
                class="badge bg-secondary ms-1"
                role="button"
                :data-test="'examinations-log-dismissed-' + row.case_no"
                :title="dismissedTooltip(row)"
                @click="toggleDismiss(row)"
              >
                Dismissed
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row-fluid pull-right">
        <DerPaginate
          v-model="system.paginate.current_page"
          :page-count="system.paginate.last_page"
          :show-go="true"
          @change="(p) => getTransfers(p)"
        />
      </div>
    </div>

    <examination-detail-modal
      v-if="detail.visible"
      :examination-id="detail.examinationId"
      @close="detail.visible = false"
    />
  </div>
</template>

<script>
import { practitionerHttpService } from "../../../../app/practitioner-portal/store/practitioner-http-service";
import ClipLoader from "../../../Utils/ClipLoader.vue";
import ExaminationDetailModal from "./ExaminationDetailModal.vue";
import DerPaginate from "../../../../design-system/DerPaginate.vue";
import CustomDatepicker from "../../../../components/Utils/Datepicker.vue";
import GenericAdminFilter from "../../Filters/GenericAdminFilter.vue";

export default {
  components: {
    ClipLoader,
    ExaminationDetailModal,
    DerPaginate,
    CustomDatepicker,
    GenericAdminFilter,
  },
  data() {
    return {
      showFilters: false,
      loading: false,
      centers: [],
      selectKeys: { center: 0 },
      filters: {
        case_no: "",
        product_id: null,
        center_id: null,
        user: "",
        status_id: null,
        patient: "",
        medium: "",
        part: "",
        dateFrom: null,
        dateUntil: null,
      },
      system: {
        rows: [],
        paginate: { current_page: 1, total: 0, last_page: 1 },
      },
      detail: { visible: false, examinationId: null },
    };
  },
  created() {
    this.fetchCenters();
    this.getTransfers(1);
  },
  methods: {
    fetchCenters() {
      practitionerHttpService
        .get("/api/internal/admin/all-centers")
        .then((res) => {
          this.centers = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.centers = [];
        });
    },
    idOrUndefined(v) {
      if (v === null || v === undefined) return undefined;
      if (typeof v === "string" && v.trim() === "") return undefined;
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return n;
    },
    getTransfers(page = 1) {
      this.loading = true;
      practitionerHttpService
        .get("/api/internal/admin/logs/examinations", {
          params: {
            page,
            case_no: this.filters.case_no || undefined,
            product_id: this.idOrUndefined(this.filters.product_id),
            center_id: this.idOrUndefined(this.filters.center_id),
            user: this.filters.user || undefined,
            patient: this.filters.patient || undefined,
            status_id: this.idOrUndefined(this.filters.status_id),
            medium: this.filters.medium || undefined,
            part: this.filters.part || undefined,
            date_from: this.serializeDate(this.filters.dateFrom),
            date_until: this.serializeDate(this.filters.dateUntil),
          },
        })
        .then((res) => {
          const payload = res.data;
          this.system.rows = payload.data;
          this.system.paginate = payload.paginate;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.userErrorMessage(error);
        });
    },
    applyFilters() {
      this.system.paginate.current_page = 1;
      this.getTransfers(1);
    },
    clearFilters() {
      this.filters = {
        case_no: "",
        product_id: null,
        center_id: null,
        user: "",
        patient: "",
        status_id: "",
        medium: "",
        part: "",
        dateFrom: null,
        dateUntil: null,
      };
      this.selectKeys.center += 1;
      this.system.paginate.current_page = 1;
      this.getTransfers(1);
    },
    changeDates(key, value) {
      this.filters[key] = value;
    },
    serializeDate(d) {
      if (!d) return undefined;
      if (typeof d === "string") return d;
      if (d instanceof Date && !isNaN(d.getTime()))
        return d.toISOString().slice(0, 10);
      return undefined;
    },
    rowClass(row) {
      const s = (row.status_name || "").toString().toLowerCase();
      if (s === "failed") return "table-danger";
      if (s === "draft") return "table-warning";
      if (s === "removed") return "table-removed";
      return "table-success";
    },
    openDetail(id) {
      if (!id) return;
      this.detail.examinationId = id;
      this.detail.visible = true;
    },
    canDismiss(row) {
      const s = (row.status_name || "").toString().toLowerCase();
      const isPriority = s === "failed" || s === "draft";
      return isPriority && !row.dismissed_at;
    },
    dismissedTooltip(row) {
      const who =
        row.dismissed_by_name ||
        (row.dismissed_by_user_id
          ? `User #${row.dismissed_by_user_id}`
          : "Unknown");
      const when = row.dismissed_at;
      return `${who} • ${when}\nClick to restore to priority sorting.`;
    },
    toggleDismiss(row) {
      if (!row.examination_id) return;
      const wasDismissed = !!row.dismissed_at;
      const backup = { ...row };
      row.dismissed_at = wasDismissed ? null : new Date().toISOString();

      practitionerHttpService
        .post(
          `/api/internal/admin/logs/examinations/${row.examination_id}/dismiss`,
        )
        .then((res) => {
          row.dismissed_at = res.data.dismissed_at;
          row.dismissed_by_user_id = res.data.dismissed_by_user_id;
          row.dismissed_by_name = res.data.dismissed_by_name;
          this.getTransfers(this.system.paginate.current_page);
        })
        .catch((err) => {
          Object.assign(row, backup);
          this.userErrorMessage(err);
        });
    },
    copyToClipboard(value) {
      if (!value) return;
      navigator.clipboard.writeText(value);
    },
  },
};
</script>

<style scoped>
.filter-box {
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 0.5rem;
  padding: 1rem 1rem 1.25rem 1rem;
}
.filter-actions {
  gap: 0.5rem;
}
.table-danger {
  background-color: #f8d7da;
}
.table-success {
  background-color: #d4edda;
}
.table-warning {
  background-color: #fff3cd;
}
.table-removed {
  background-color: #e2e3e5;
}
.dismissed-row {
  opacity: 0.3;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
